import {
  AnalyticsBrowser,
  type AnalyticsBrowserSettings,
  type InitOptions,
} from '@segment/analytics-next';
import { string as zString } from 'zod';
import { resolveFeatureFlag } from '@hungryroot/feature-flags';
import { observeSegmentLoad, probeSegmentDelivery } from './segmentDelivery';

const PUBLIC_SEGMENT_KEY = zString().parse(process.env.PUBLIC_SEGMENT_KEY);

// First-party proxy hosts, used to route Segment around ad/tracker blockers.
// Staging always proxies; production proxies only when the
// `web.feature-flag.segment-proxy.enabled` feature flag is enabled. Events go
// to the api host; CDN settings come from the evs host.
const PROXY_HOSTS = {
  staging: {
    apiHost: 'api.analytics.staging.hungryroot.com/v1',
    cdnURL: 'https://evs.analytics.staging.hungryroot.com',
  },
  production: {
    apiHost: 'api.analytics.hungryroot.com/v1',
    cdnURL: 'https://evs.analytics.hungryroot.com',
  },
} as const;

type ProxyHosts = (typeof PROXY_HOSTS)[keyof typeof PROXY_HOSTS];

// Where analytics-next posts events when no proxy is configured. Mirrored here
// so the delivery probe can target the host actually in use.
const SEGMENT_DEFAULT_API_HOST = 'api.segment.io/v1';

// `/t` is the track endpoint events POST to, so a blocklist rule that stops
// delivery also stops this probe.
function resolveDeliveryProbeUrl(proxyHosts: ProxyHosts | null): string {
  return `https://${proxyHosts?.apiHost ?? SEGMENT_DEFAULT_API_HOST}/t`;
}

const isStaging = process.env.PUBLIC_HR_ENV === 'staging';
const isProduction = process.env.PUBLIC_HR_ENV === 'production';

// Buffered instance: track/page/identify calls made before load() resolves are
// queued and flushed once the proxy decision is made. This lets us defer the
// load until the async feature-flag check completes without dropping events.
//
// For the "track" function, the standard the team agreed upon for event names
// is Title Case (spacing allowed).
export const analytics = new AnalyticsBrowser();

function buildLoadArgs(
  proxyHosts: ProxyHosts | null
): [AnalyticsBrowserSettings, InitOptions] {
  // Deliver events with `keepalive` so ones fired during page teardown survive
  // it. The `tab_session` `ended` event fires on `visibilitychange: hidden` /
  // `pagehide`; without keepalive it's a plain fetch the browser cancels when the
  // tab actually closes — the cause of ~15% of web tab sessions never delivering
  // an end event. (Native iOS transport isn't torn down the same way, so iOS is
  // unaffected.)
  if (proxyHosts === null) {
    return [
      { writeKey: PUBLIC_SEGMENT_KEY },
      {
        integrations: {
          'Segment.io': {
            deliveryStrategy: {
              strategy: 'standard',
              config: { keepalive: true },
            },
          },
        },
      },
    ];
  }

  return [
    { writeKey: PUBLIC_SEGMENT_KEY, cdnURL: proxyHosts.cdnURL },
    {
      integrations: {
        'Segment.io': {
          apiHost: proxyHosts.apiHost,
          protocol: 'https',
          deliveryStrategy: {
            strategy: 'standard',
            config: { keepalive: true },
          },
        },
      },
    },
  ];
}

async function resolveProxyHosts(): Promise<ProxyHosts | null> {
  if (isStaging) {
    return PROXY_HOSTS.staging;
  }

  if (isProduction) {
    const enabled = await resolveFeatureFlag(
      'web.feature-flag.segment-proxy.enabled'
    );
    if (enabled === true) {
      return PROXY_HOSTS.production;
    }
  }

  return null;
}

async function initAnalytics() {
  const proxyHosts = await resolveProxyHosts();
  const [settings, options] = buildLoadArgs(proxyHosts);
  // Both outcomes feed what `/api/v3/search/` reports as `X-Segment-Disabled`.
  // The probe runs in parallel: it targets a different host than the load, and
  // neither result depends on the other. See `./segmentDelivery`.
  await Promise.all([
    observeSegmentLoad(analytics.load(settings, options)),
    probeSegmentDelivery(resolveDeliveryProbeUrl(proxyHosts)),
  ]);
}

// Browser-only, and guarded because this is a module side effect: importing
// anything from this file runs it. Astro renders every page on the server, so
// without the guard `analytics.load()` runs where there is no browser to load
// into, fails, and leaves `loadOk`/`deliveryOk` false — which is the same state
// `getIsSegmentDisabled()` reads as "Segment is disabled". The flags are only
// ever consumed from the client (`useOpenSearchSearch`), so a server-side run
// contributes nothing and can only be wrong.
if (typeof window !== 'undefined') {
  void initAnalytics();
}
